import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { SolcellerFormData } from '../types';
import { estimateTotalInstalledKwp } from '../utils/estimateInstalledKwp';

/** Max plausible specific yield for roof PV in Norway (kWh per kWp per year). */
export const MAX_SPECIFIC_YIELD_KWH_PER_KWP = 1200;

/** Min plausible specific yield for roof PV in Norway (kWh per kWp per year). */
export const MIN_SPECIFIC_YIELD_KWH_PER_KWP = 200;

/** Sum kWp above this shows a non-blocking warning (typical residential systems are smaller). */
export const KWP_SOFT_WARNING_TOTAL = 30;

/** Upper bound for "how many homes share this installation" in the portal form. */
export const MAX_TOTAL_SHARED_BOENHETER = 30;

const getDistributionFactor = (formData: SolcellerFormData): number | undefined => {
  if (formData.sharedSolarInstallation !== 'yes') {
    return 1;
  }

  if (formData.sharedSolarDistribution === 'equal') {
    const totalSharedBoenheter = formData.totalSharedBoenheter;
    return totalSharedBoenheter && totalSharedBoenheter >= 2 ? 1 / totalSharedBoenheter : undefined;
  }

  if (formData.sharedSolarDistribution === 'percentage') {
    const fordelingsnokkelPercent = formData.fordelingsnokkelPercent;
    return fordelingsnokkelPercent != null &&
      !Number.isNaN(fordelingsnokkelPercent) &&
      fordelingsnokkelPercent >= 1 &&
      fordelingsnokkelPercent <= 99
      ? fordelingsnokkelPercent / 100
      : undefined;
  }

  return undefined;
};

export const useSolcellerValidation = () => {
  const { t } = useTranslation();

  const validateTilt = (value?: number | string) => {
    if (value === undefined || value === '' || Number.isNaN(value)) {
      return t('register.solar.form.panel.tilt.missing');
    }

    const numericValue = typeof value === 'number' ? value : Number(value);

    if (Number.isNaN(numericValue) || numericValue > 90 || numericValue < 0) {
      return t('register.solar.form.panel.tilt.invalid');
    }
    return true;
  };

  const validateAzimuth = (value?: number | string) => {
    if (value === undefined || value === '' || Number.isNaN(value)) {
      return t('register.solar.form.panel.azimuth.missing');
    }

    const numericValue = typeof value === 'number' ? value : Number(value);

    if (Number.isNaN(numericValue) || numericValue > 359 || numericValue < 0) {
      return t('register.solar.form.panel.azimuth.invalid');
    }
    return true;
  };

  const validateArea = (value?: number) => {
    if (!value) {
      return t('register.solar.form.panel.area.missing');
    }
    if (value > 200 || value <= 0) {
      return t('register.solar.form.panel.area.invalid');
    }
    return true;
  };

  const validateKwp = (value?: number) => {
    if (value === undefined || Number.isNaN(value)) {
      return t('register.solar.form.panel.effect.missing');
    }
    if (value <= 0 || value > 100) {
      return t('register.solar.form.panel.effect.invalid');
    }
    return true;
  };

  const validateKwh = (value?: number) => {
    if (value === undefined || Number.isNaN(value)) {
      return t('register.solar.form.productionData.error');
    }
    if (value <= 0) {
      return t('register.solar.form.productionData.mustBePositive');
    }
    if (value > 100000) {
      return t('register.solar.form.productionData.invalid');
    }
    return true;
  };

  const validateKwhWithSpecificYieldCap = useCallback(
    (value: number | undefined, getFormValues: () => SolcellerFormData): string | true => {
      if (value === undefined || Number.isNaN(value)) {
        return t('register.solar.form.productionData.error');
      }
      if (value <= 0) {
        return t('register.solar.form.productionData.mustBePositive');
      }
      if (value > 100000) {
        return t('register.solar.form.productionData.invalid');
      }
      const formData = getFormValues();
      const totalKwp = estimateTotalInstalledKwp(formData);
      if (totalKwp <= 0) {
        return true;
      }
      const distributionFactor = getDistributionFactor(formData);
      const effectiveKwp =
        formData.sharedSolarInstallation === 'yes' && formData.productionDataScope === 'thisBoenhet'
          ? totalKwp * (distributionFactor ?? 0)
          : totalKwp;
      if (effectiveKwp <= 0) {
        return true;
      }
      const maxKwh = MAX_SPECIFIC_YIELD_KWH_PER_KWP * effectiveKwp;
      if (value > maxKwh) {
        return t('register.solar.form.productionData.specificYieldExceeded', {
          maxKwh: Math.round(maxKwh)
        });
      }
      const minKwh = MIN_SPECIFIC_YIELD_KWH_PER_KWP * effectiveKwp;
      if (value < minKwh) {
        return t('register.solar.form.productionData.specificYieldBelowMinimum', {
          minKwh: Math.round(minKwh)
        });
      }
      return true;
    },
    [t]
  );

  const validateTotalSharedBoenheter = (value?: number) => {
    if (value === undefined || Number.isNaN(value)) {
      return t('register.solar.form.sharing.totalUnits.error');
    }
    if (!Number.isInteger(value) || value < 2 || value > MAX_TOTAL_SHARED_BOENHETER) {
      return t('register.solar.form.sharing.totalUnits.invalid');
    }
    return true;
  };

  const validateDistributionPercent = (value?: number) => {
    if (value === undefined || Number.isNaN(value)) {
      return t('register.solar.form.sharing.percentage.error');
    }
    if (value < 1 || value > 99) {
      return t('register.solar.form.sharing.percentage.invalid');
    }
    return true;
  };

  const decimalPattern = {
    value: /^\d+(?:[.,]\d{1,2})?$/,
    message: t('form.error.decimal')
  };

  const integerPattern = {
    value: /^\d+$/,
    message: t('form.error.integer')
  };

  return {
    integerPattern,
    decimalPattern,
    validateTilt,
    validateAzimuth,
    validateArea,
    validateDistributionPercent,
    validateKwh,
    validateKwhWithSpecificYieldCap,
    validateTotalSharedBoenheter,
    validateKwp
  };
};
